import React, { useMemo } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, MARKS, INLINES } from "@contentful/rich-text-types";
import ErrorComponent from "@components/Error";
import CheckMarkGreen from "../../static/images/icons/check_mark_green.svg";
import CheckMarkRed from "../../static/images/icons/check_mark_red.svg";
import tailwindConfig from "../../../tailwind.config";
import Cta from "@components/CTA";

// Define a function to get colors from Tailwind config directly
const getColorFromConfig = (colorName) => {
    if (!colorName) return null;
    try {
        const colors = tailwindConfig.theme?.colors || {};
        return colors[colorName?.toLowerCase()] || colors.text_paragraph || "inherit";
    } catch (e) {
        console.error("Error getting color from config", e);
        return "inherit";
    }
};

// Simple styled component for the text container
const StyledTextContainer = styled.div`
  /* Desktop padding and margins - only apply if props exist */
  ${props => props.$marginTopDesktop !== undefined && props.$marginTopDesktop !== null && `margin-top: ${props.$marginTopDesktop}px;`}
  ${props => props.$marginBottomDesktop !== undefined && props.$marginBottomDesktop !== null && `margin-bottom: ${props.$marginBottomDesktop}px;`}
  ${props => props.$paddingTopDesktop !== undefined && props.$paddingTopDesktop !== null && `padding-top: ${props.$paddingTopDesktop}px;`}
  ${props => props.$paddingBottomDesktop !== undefined && props.$paddingBottomDesktop !== null && `padding-bottom: ${props.$paddingBottomDesktop}px;`}
  ${props => props.$paddingLeftDesktop !== undefined && props.$paddingLeftDesktop !== null && `padding-left: ${props.$paddingLeftDesktop}px;`}
  ${props => props.$paddingRightDesktop !== undefined && props.$paddingRightDesktop !== null && `padding-right: ${props.$paddingRightDesktop}px;`}
  ${props => props.$maxWidthDesktop !== undefined && props.$maxWidthDesktop !== null && `max-width: ${props.$maxWidthDesktop}px;`}
  ${props => props.$center && `
    margin-left: auto;
    margin-right: auto;
  `}
  ${props => props?.$textAlign !== undefined && `text-align: ${props?.$textAlign?.toLowerCase()};`}
  ${props => props?.$textColor !== undefined && `color: ${getColorFromConfig(props?.$textColor)};`}

  /* Mobile styles - only apply if props exist, with desktop fallbacks */
  @media (max-width: 1023px) {
    ${props => (props.$marginTopMobile !== undefined && props.$marginTopMobile !== null ?
        `margin-top: ${props.$marginTopMobile}px;` :
        props.$marginTopDesktop !== undefined && props.$marginTopDesktop !== null && `margin-top: ${props.$marginTopDesktop}px;`
    )}
    ${props => (props.$marginBottomMobile !== undefined && props.$marginBottomMobile !== null ?
        `margin-bottom: ${props.$marginBottomMobile}px;` :
        props.$marginBottomDesktop !== undefined && props.$marginBottomDesktop !== null && `margin-bottom: ${props.$marginBottomDesktop}px;`
    )}
    ${props => (props.$paddingTopMobile !== undefined && props.$paddingTopMobile !== null ?
        `padding-top: ${props.$paddingTopMobile}px;` :
        props.$paddingTopDesktop !== undefined && props.$paddingTopDesktop !== null && `padding-top: ${props.$paddingTopDesktop}px;`
    )}
    ${props => (props.$paddingBottomMobile !== undefined && props.$paddingBottomMobile !== null ?
        `padding-bottom: ${props.$paddingBottomMobile}px;` :
        props.$paddingBottomDesktop !== undefined && props.$paddingBottomDesktop !== null && `padding-bottom: ${props.$paddingBottomDesktop}px;`
    )}
    ${props => (props.$paddingLeftMobile !== undefined && props.$paddingLeftMobile !== null ?
        `padding-left: ${props.$paddingLeftMobile}px;` :
        props.$paddingLeftDesktop !== undefined && props.$paddingLeftDesktop !== null && `padding-left: ${props.$paddingLeftDesktop}px;`
    )}
    ${props => (props.$paddingRightMobile !== undefined && props.$paddingRightMobile !== null ?
        `padding-right: ${props.$paddingRightMobile}px;` :
        props.$paddingRightDesktop !== undefined && props.$paddingRightDesktop !== null && `padding-right: ${props.$paddingRightDesktop}px;`
    )}
    ${props => (props.$maxWidthMobile !== undefined && props.$maxWidthMobile !== null ?
        `max-width: ${props.$maxWidthMobile}px;` :
        props.$maxWidthDesktop !== undefined && props.$maxWidthDesktop !== null && `max-width: ${props.$maxWidthDesktop}px;`
    )}
  }

`;

/**
 * TextComponent - A versatile component for rendering rich text content from Contentful
 * 
 * @param {Object} props - Component props
 * @param {Object} props.content - Rich text content from Contentful (JSON format)
 * @param {Array} props.references - References to embedded entries in the content
 * @param {string} props.style - CSS classes to apply to the container element
 * @param {Object} props.nodeStyles - Specific styles for different node types (e.g. {heading1: "text-blue-500", paragraph: "mb-4"})
 * @param {Object} props.customRenderers - Custom renderers for specific node types
 * @param {Object} props.customMarks - Custom renderers for specific mark types
 * @param {Function} props.onError - Callback for error handling
 * @returns {React.ReactElement} Rendered rich text content
 */
const TextComponent = ({
    content,
    references = [],
    style = '',
    nodeStyles = {},
    customRenderers = {},
    customMarks = {},
    onError,
    marginTopDesktop,
    marginBottomDesktop,
    marginTopMobile,
    marginBottomMobile,
    paddingTopDesktop,
    paddingBottomDesktop,
    paddingTopMobile,
    paddingBottomMobile,
    paddingLeftDesktop,
    paddingRightDesktop,
    paddingLeftMobile,
    paddingRightMobile,
    maxWidthDesktop,
    maxWidthMobile,
    classOverlay,
    textAlign,
    textColor,
    ...otherProps
}) => {

    // Create a map of references for quick lookup
    const referencesMap = useMemo(() => {
        const map = {};
        if (references && references.length > 0) {
            references.forEach(reference => {
                if (reference.contentful_id) {
                    map[reference.contentful_id] = reference;
                }
            });
        }
        return map;
    }, [references]);

    // Create render options with memoization for performance
    const options = useMemo(() =>
        createRenderOptions(nodeStyles, referencesMap, customRenderers, customMarks, textColor, textAlign),
        [nodeStyles, referencesMap, customRenderers, customMarks, textColor, textAlign]
    );

    // Don't render if content is undefined or empty
    if (!content || content === '') {
        return <ErrorComponent message="No content available to display" />;
    }

    // Process content if it exists
    try {
        const parsedContent = typeof content === 'string' ? JSON.parse(content) : content;

        // Filter out props that shouldn't be in the DOM
        const {
            content: contentProp, // Avoid passing content as a DOM attribute
            references: refsProp,
            nodeStyles: nodeStylesProp,
            customRenderers: renderersProp,
            customMarks: marksProp,
            onError: errorProp,
            ...domProps
        } = otherProps;

        return (
            <StyledTextContainer
                $marginTopDesktop={marginTopDesktop}
                $marginBottomDesktop={marginBottomDesktop}
                $marginTopMobile={marginTopMobile}
                $marginBottomMobile={marginBottomMobile}
                $paddingTopDesktop={paddingTopDesktop}
                $paddingBottomDesktop={paddingBottomDesktop}
                $paddingTopMobile={paddingTopMobile}
                $paddingBottomMobile={paddingBottomMobile}
                $paddingLeftDesktop={paddingLeftDesktop}
                $paddingRightDesktop={paddingRightDesktop}
                $paddingLeftMobile={paddingLeftMobile}
                $paddingRightMobile={paddingRightMobile}
                $maxWidthDesktop={maxWidthDesktop}
                $maxWidthMobile={maxWidthMobile}
                $textAlign={textAlign}
                $textColor={textColor}
                {...domProps}
                className={`${style}${classOverlay ? ' ' + classOverlay : ''}`}
                data-component="text-component"
            >
                {documentToReactComponents(parsedContent, options)}
            </StyledTextContainer>
        );

    } catch (error) {
        const errorMessage = "Error processing content: " + error.message;
        console.error(errorMessage, error);
        if (onError) onError(errorMessage, error);
        return <ErrorComponent message={errorMessage} />;
    }
};

/**
 * Helper function to render text nodes with proper formatting
 * @param {Object} contentNode - The content node to render
 * @param {number} index - Index for key generation
 * @param {string} keyPrefix - Prefix for the React key
 * @param {Object} customMarks - Custom renderers for specific mark types
 * @returns {React.ReactElement} Formatted text element
 */
const renderTextNode = (contentNode, index, keyPrefix = '', customMarks = {}) => {
    // If this is a hyperlink node, handle it differently
    if (contentNode.nodeType === 'hyperlink') {
        const uri = contentNode.data?.uri;
        const linkContent = contentNode.content && contentNode.content.length > 0
            ? contentNode.content[0].value
            : '';

        if (uri) {
            return (
                <Cta
                    key={`${keyPrefix}-link-${index}`}
                    displayType="Link"
                    href={uri}
                    label={linkContent}
                    target={(typeof window === 'undefined' ? uri.startsWith('/') : uri.startsWith('/') || uri.includes(window.location.hostname)) ? "Same" : "New"}
                    className="text-primary underline hover:no-underline"
                />
            );
        }
        return linkContent;
    }

    // Check for marks
    const hasBold = contentNode.marks?.some(mark => mark.type === 'bold');
    const hasSuperscript = contentNode.marks?.some(mark => mark.type === 'superscript');
    const hasSubscript = contentNode.marks?.some(mark => mark.type === 'subscript');
    const hasItalic = contentNode.marks?.some(mark => mark.type === 'italic');
    const hasUnderline = contentNode.marks?.some(mark => mark.type === 'underline');
    const hasCode = contentNode.marks?.some(mark => mark.type === 'code');

    // Check for custom marks
    const customMarkTypes = contentNode.marks?.filter(mark =>
        customMarks[mark.type] && typeof customMarks[mark.type] === 'function'
    ).map(mark => mark.type) || [];

    let content = contentNode.value;

    // Handle newlines in text
    if (typeof content === 'string' && content.includes('\n')) {
        content = content.split('\n').map((segment, i) => (
            <React.Fragment key={`text-nl-${i}`}>
                {segment}
                {i < content.split('\n').length - 1 && <br />}
            </React.Fragment>
        ));
    }

    // Clean up any remaining marker text that may have been missed
    if (typeof content === 'string') {
        // Check for marker patterns and remove them - more targeted approach
        const checkmarkMarkerPattern = /\[checkmark-list\]|\[checkmark-list(?!\w)|\bcheckmark-list\](?!\w)|\bcheckmark-list\b/g;
        content = content.replace(checkmarkMarkerPattern, '');

        // Check for cross marker patterns
        const crossMarkerPattern = /\[cross-list\]|\[cross-list(?!\w)|\bcross-list\](?!\w)|\bcross-list\b/g;
        content = content.replace(crossMarkerPattern, '');

        // Remove stray brackets only if they look like they're from a broken marker
        if (content.trim().startsWith('[') && content.length < 5) {
            content = content.replace(/^\s*\[+\s*/, '');
        }
        if (content.trim().endsWith(']') && content.length < 5) {
            content = content.replace(/\s*\]+\s*$/, '');
        }
    }

    // Apply custom marks first
    customMarkTypes.forEach(markType => {
        content = customMarks[markType](content);
    });

    // Apply standard formatting if needed
    if (hasCode) content = <code>{content}</code>;
    if (hasUnderline) content = <u>{content}</u>;
    if (hasItalic) content = <i>{content}</i>;
    if (hasBold) content = <b>{content}</b>;

    // For superscript and subscript formatting (applied last)
    if (hasSuperscript) {
        return <sup key={`${keyPrefix}-${index}`}>{content}</sup>;
    }

    if (hasSubscript) {
        return <sub key={`${keyPrefix}-${index}`}>{content}</sub>;
    }

    // Return content with appropriate formatting
    return content;
};

/**
 * Renders an embedded entry based on its type
 * @param {Object} reference - The reference data
 * @param {Object} customRenderers - Custom renderers for embedded entries
 * @returns {React.ReactElement} Rendered embedded entry
 */
const renderEmbeddedEntry = (reference, customRenderers = {}) => {
    // Return null if reference is not provided
    if (!reference) return null;

    // Get the content type
    const contentType = reference.__typename;

    // Check if there's a custom renderer for this content type
    if (customRenderers[contentType] && typeof customRenderers[contentType] === 'function') {
        return customRenderers[contentType](reference);
    }

    // Default rendering based on content type - don't pass style parameter
    switch (contentType) {
        case "ContentfulImage":
            return reference.file?.url ? (
                <img
                    src={reference.file.url}
                    alt={reference.title || 'Embedded image'}
                    className="my-4 max-w-full"
                />
            ) : null;

        case "ContentfulVideo":
            return reference.embedUrl ? (
                <div className="my-4 aspect-video">
                    <iframe
                        src={reference.embedUrl}
                        title={reference.title || 'Embedded video'}
                        className="w-full h-full"
                        frameBorder="0"
                        allowFullScreen
                    />
                </div>
            ) : null;

        default:
            // For other types, render a simple representation
            return (
                <div className="embedded-entry">
                    {reference.title && <p className="font-bold">{reference.title}</p>}
                    {reference.text?.raw && documentToReactComponents(JSON.parse(reference.text.raw))}
                </div>
            );
    }
};

/**
 * Create render options for documentToReactComponents
 * @param {Object} nodeStyles - Specific styles for different node types
 * @param {Object} referencesMap - Map of references for embedded entries
 * @param {Object} customRenderers - Custom renderers for specific node types
 * @param {Object} customMarks - Custom renderers for specific mark types
 * @param {string} textColor - The text color to apply
 * @param {string} textAlign - The text alignment to apply
 * @returns {Object} Options for rendering
 */
const createRenderOptions = (nodeStyles = {}, referencesMap = {}, customRenderers = {}, customMarks = {}, textColor = '', textAlign = '') => ({
    // Add renderText option to handle text content
    renderText: text => {
        if (!text) return '';
        return text;
    },
    renderNode: {
        // Apply custom renderers first
        ...Object.entries(customRenderers).reduce((acc, [nodeType, renderer]) => {
            if (typeof renderer === 'function') {
                acc[nodeType] = renderer;
            }
            return acc;
        }, {}),

        // Standard node renderers
        [BLOCKS.HEADING_1]: (node, index) => {
            if (!node.content || node.content.length === 0) {
                return null;
            }

            // Check if this heading has a marker
            let headingStyle = '';
            let content = [...node.content]; // Clone to avoid mutating original

            if (node.content[0]?.nodeType === 'text') {
                const text = node.content[0].value || '';

                // Check for h1 style markers
                if (text.startsWith('[h1_4xl]')) {
                    headingStyle = `text-h1_4xl ${nodeStyles.h1_4xl || ''}`.trim();
                    content[0] = { ...node.content[0], value: text.substring(9) };
                } else if (text.startsWith('[h1_3xl]')) {
                    headingStyle = `text-h1_3xl ${nodeStyles.h1_3xl || ''}`.trim();
                    content[0] = { ...node.content[0], value: text.substring(9) };
                } else if (text.startsWith('[h1_2xl]')) {
                    headingStyle = `text-h1_2xl ${nodeStyles.h1_2xl || ''}`.trim();
                    content[0] = { ...node.content[0], value: text.substring(9) };
                } else if (text.startsWith('[h1_xl]')) {
                    headingStyle = `text-h1_xl font-asap_condensed ${nodeStyles.h1_xl || ''}`.trim();
                    content[0] = { ...node.content[0], value: text.replace('[h1_xl]', '') };
                } else if (text.startsWith('[h1_40]')) {
                    headingStyle = `text-h1_40 lg:text-h1_64 font-asap_condensed ${nodeStyles.h1_xl || ''}`.trim();
                    content[0] = { ...node.content[0], value: text.replace('[h1_40]', '') };
                } else if (text.startsWith('[h1_lg]')) {
                    headingStyle = `text-h1_lg ${nodeStyles.h1_lg || ''}`.trim();
                    content[0] = { ...node.content[0], value: text.substring(8) };
                } else {
                    // Default h1 style
                    headingStyle = `text-h1 ${nodeStyles.heading1 || ''}`.trim();
                }
            } else {
                // Default heading style if no text node at the beginning
                headingStyle = `text-h1 ${nodeStyles.heading1 || ''}`.trim();
            }

            return (
                <h1 key={`heading-${node.nodeType}-${index}`} className={headingStyle}>
                    {content.map((contentNode, index) =>
                        renderTextNode(contentNode, index, 'h1', customMarks)
                    )}
                </h1>
            );
        },
        [BLOCKS.HEADING_2]: (node, index) => {
            if (!node.content || node.content.length === 0) {
                return null;
            }

            // Apply base style and node-specific style
            let headingStyle = '';
            let content = [...node.content]; // Clone to avoid mutating original

            if (node.content[0]?.nodeType === 'text') {
                const text = node.content[0].value || '';

                // Check for h2 style markers
                if (text.startsWith('[h2]')) {
                    headingStyle = `text-h4 lg:text-h2 ${nodeStyles.h2 || ''}`.trim();
                    content[0] = { ...node.content[0], value: text.substring(4) };
                } else {
                    // Default h2 style
                    headingStyle = `text-h4 lg:text-h2 ${nodeStyles.heading2 || ''}`.trim();
                }
            } else {
                // Default heading style if no text node at the beginning
                headingStyle = `text-h4 lg:text-h2 ${nodeStyles.heading2 || ''}`.trim();
            }

            return (
                <h2 key={`heading-${node.nodeType}-${index}`} className={headingStyle}>
                    {content.map((contentNode, index) =>
                        renderTextNode(contentNode, index, 'h2', customMarks)
                    )}
                </h2>
            );
        },
        [BLOCKS.HEADING_3]: (node, index) => {
            if (!node.content || node.content.length === 0) {
                return null;
            }

            // Apply base style and node-specific style
            let headingStyle = '';
            let content = [...node.content]; // Clone to avoid mutating original

            if (node.content[0]?.nodeType === 'text') {
                const text = node.content[0].value || '';

                // Check for h3 style markers
                if (text.startsWith('[h3]')) {
                    headingStyle = `text-h3 ${nodeStyles.h3 || ''}`.trim();
                    content[0] = { ...node.content[0], value: text.substring(4) };
                } else {
                    // Default h3 style
                    headingStyle = `text-h3 ${nodeStyles.heading3 || ''}`.trim();
                }
            } else {
                // Default heading style if no text node at the beginning
                headingStyle = `text-h3 ${nodeStyles.heading3 || ''}`.trim();
            }

            return (
                <h3 key={`heading-${node.nodeType}-${index}`} className={headingStyle}>
                    {content.map((contentNode, index) =>
                        renderTextNode(contentNode, index, 'h3', customMarks)
                    )}
                </h3>
            );
        },
        [BLOCKS.HEADING_4]: (node, index) => {
            if (!node.content || node.content.length === 0) {
                return null;
            }

            // Apply base style and node-specific style
            let headingStyle = '';
            let content = [...node.content]; // Clone to avoid mutating original

            if (node.content[0]?.nodeType === 'text') {
                const text = node.content[0].value || '';

                // Check for h4 style markers
                if (text.startsWith('[h4_medium]')) {
                    headingStyle = `text-h4_medium_mobile lg:text-h4_medium ${nodeStyles.h4_medium || ''}`.trim();
                    content[0] = { ...node.content[0], value: text.substring(11) };
                } else {
                    // Default h4 style
                    headingStyle = `text-h4_medium_mobile lg:text-h4 ${nodeStyles.heading4 || ''}`.trim();
                }
            } else {
                // Default heading style if no text node at the beginning
                headingStyle = `text-h4 ${nodeStyles.heading4 || ''}`.trim();
            }

            return (
                <h4 key={`heading-${node.nodeType}-${index}`} className={headingStyle}>
                    {content.map((contentNode, index) =>
                        renderTextNode(contentNode, index, 'h4', customMarks)
                    )}
                </h4>
            );
        },
        [BLOCKS.HEADING_6]: (node, index) => {
            if (!node.content || node.content.length === 0) {
                return null;
            }

            // Apply base style and node-specific style
            let headingStyle = '';
            let content = [...node.content]; // Clone to avoid mutating original

            if (node.content[0]?.nodeType === 'text') {
                const text = node.content[0].value || '';

                // Check for h6 style markers
                if (text.startsWith('[h6]')) {
                    headingStyle = `text-h6 ${nodeStyles.h6 || ''}`.trim();
                    content[0] = { ...node.content[0], value: text.substring(4) };
                } else {
                    // Default h6 style
                    headingStyle = `text-h6 ${nodeStyles.heading6 || ''}`.trim();
                }
            } else {
                // Default heading style if no text node at the beginning
                headingStyle = `text-h6 ${nodeStyles.heading6 || ''}`.trim();
            }

            return (
                <h6 key={`heading-${node.nodeType}-${index}`} className={headingStyle}>
                    {content.map((contentNode, index) =>
                        renderTextNode(contentNode, index, 'h6', customMarks)
                    )}
                </h6>
            );
        },
        [BLOCKS.PARAGRAPH]: (node, index) => {
            if (!node.content || node.content.length === 0 || node.content.every(item => !item.value || item.value.trim() === '')) {
                return null;
            }

            // Check if this paragraph should use special text style
            // Look for text markers at the start of the paragraph
            let paragraphStyle = '';
            let content = [...node.content]; // Clone to avoid mutating original

            if (node.content[0]?.nodeType === 'text') {
                const text = node.content[0].value || '';

                // Check for paragraph style markers
                if (text.startsWith('[p_medium]')) {
                    paragraphStyle = `text-p_medium ${nodeStyles.p_medium || ''}`.trim();
                    content[0] = { ...node.content[0], value: text.substring(10) };
                } else if (text.startsWith('[small]') || text.startsWith('[s]')) {
                    // Apply small text style
                    paragraphStyle = `text-p_footnote ${nodeStyles.smallParagraph || ''}`.trim();

                    // Remove the marker from the displayed text
                    if (text.startsWith('[small]')) {
                        content[0] = { ...node.content[0], value: text.substring(7) };
                    } else {
                        content[0] = { ...node.content[0], value: text.substring(3) };
                    }
                } else if (text.startsWith('[large]') || text.startsWith('[l]')) {
                    // Apply large text style
                    paragraphStyle = `text-xl ${nodeStyles.largeParagraph || ''}`.trim();

                    // Remove the marker from the displayed text
                    if (text.startsWith('[large]')) {
                        content[0] = { ...node.content[0], value: text.substring(7) };
                    } else {
                        content[0] = { ...node.content[0], value: text.substring(3) };
                    }
                } else {
                    // Default paragraph style
                    paragraphStyle = `text-p_mobile lg:text-p ${nodeStyles.paragraph || ''}`.trim();
                }
            } else {
                // Default paragraph style if no text node at the beginning
                paragraphStyle = `text-p_mobile lg:text-p ${nodeStyles.paragraph || ''}`.trim();
            }

            // Return standard paragraph
            return (
                <p key={`paragraph-${JSON.stringify(content)}`} className={paragraphStyle}>
                    {content.map((contentNode, index) =>
                        renderTextNode(contentNode, index, 'p', customMarks)
                    )}
                </p>
            );
        },
        [BLOCKS.UL_LIST]: (node) => {
            if (!node.content || node.content.length === 0) {
                return null;
            }

            // Apply base style with default bullet style
            const combinedStyle = `list-disc pl-6 ${nodeStyles.ulList || ''}`.trim();
            const containerStyle = `ul-container ${nodeStyles.ulContainer || ''}`.trim();

            return (
                <div className={containerStyle}>
                    <ul className={combinedStyle}>
                        {node.content.map((item, index) => (
                            <React.Fragment key={`list-item-${index}`}>
                                {documentToReactComponents(item, {
                                    renderNode: createRenderOptions(nodeStyles, referencesMap, customRenderers, customMarks).renderNode,
                                    renderMark: createRenderOptions(nodeStyles, referencesMap, customRenderers, customMarks).renderMark,
                                    renderText: createRenderOptions(nodeStyles, referencesMap, customRenderers, customMarks).renderText
                                })}
                            </React.Fragment>
                        ))}
                    </ul>
                </div>
            );
        },
        [BLOCKS.OL_LIST]: (node) => {
            if (!node.content || node.content.length === 0) {
                return null;
            }

            // Apply base style and node-specific style
            const combinedStyle = `list-decimal pl-8 ${nodeStyles.olList || ''}`.trim();
            const containerStyle = `ol-container ${nodeStyles.olContainer || ''}`.trim();

            return (
                <div className={containerStyle}>
                    <ol className={combinedStyle}>
                        {node.content.map((item, index) => (
                            <React.Fragment key={`list-item-${index}`}>
                                {documentToReactComponents(item, {
                                    renderNode: createRenderOptions(nodeStyles, referencesMap, customRenderers, customMarks).renderNode,
                                    renderMark: createRenderOptions(nodeStyles, referencesMap, customRenderers, customMarks).renderMark,
                                    renderText: createRenderOptions(nodeStyles, referencesMap, customRenderers, customMarks).renderText
                                })}
                            </React.Fragment>
                        ))}
                    </ol>
                </div>
            );
        },
        [BLOCKS.LIST_ITEM]: (node) => {
            if (!node.content || node.content.length === 0) {
                return null;
            }

            // Check if this list item has the checkmark marker
            let hasCheckmarkMarker = false;
            let hasCrossMarker = false;

            // Process content to remove markers
            const processedContent = node.content.map(content => {
                if (content.nodeType === BLOCKS.PARAGRAPH && content.content && content.content.length > 0) {
                    // Clone the content to avoid mutating the original
                    const newContent = { ...content };
                    const newContentNodes = [...content.content];

                    // Check first text node for markers
                    const firstTextNode = newContentNodes[0];
                    if (firstTextNode && firstTextNode.nodeType === 'text' && firstTextNode.value) {
                        const text = firstTextNode.value;

                        // Check for the exact checkmark marker
                        if (text.includes('[checkmark-list]')) {
                            hasCheckmarkMarker = true;
                            // Clean text by removing the exact marker
                            const cleanedText = text.replace(/\[checkmark-list\]/g, '');
                            newContentNodes[0] = { ...firstTextNode, value: cleanedText };
                            return { ...newContent, content: newContentNodes };
                        }

                        // Check for the exact cross marker
                        if (text.includes('[cross-list]')) {
                            hasCrossMarker = true;
                            // Clean text by removing the exact marker
                            const cleanedText = text.replace(/\[cross-list\]/g, '');
                            newContentNodes[0] = { ...firstTextNode, value: cleanedText };
                            return { ...newContent, content: newContentNodes };
                        }

                        // Check for variations or partial checkmark markers
                        const partialCheckmarkPattern = /\[checkmark-list|\bcheckmark-list\]|\bcheckmark-list\b/;
                        if (partialCheckmarkPattern.test(text)) {
                            hasCheckmarkMarker = true;
                            // Clean text by removing partial markers
                            const cleanedText = text
                                .replace(/\[checkmark-list/g, '')
                                .replace(/checkmark-list\]/g, '')
                                .replace(/\bcheckmark-list\b/g, '')
                                .replace(/^\s*\[\s*/, '') // Remove opening bracket at start
                                .replace(/\s*\]\s*$/, '') // Remove closing bracket at end
                                .replace(/^\s*\]\s*/, ''); // Remove any stray closing bracket at start

                            newContentNodes[0] = { ...firstTextNode, value: cleanedText };
                            return { ...newContent, content: newContentNodes };
                        }

                        // Check for variations or partial cross markers
                        const partialCrossPattern = /\[cross-list|\bcross-list\]|\bcross-list\b/;
                        if (partialCrossPattern.test(text)) {
                            hasCrossMarker = true;
                            // Clean text by removing partial markers
                            const cleanedText = text
                                .replace(/\[cross-list/g, '')
                                .replace(/cross-list\]/g, '')
                                .replace(/\bcross-list\b/g, '')
                                .replace(/^\s*\[\s*/, '') // Remove opening bracket at start
                                .replace(/\s*\]\s*$/, '') // Remove closing bracket at end
                                .replace(/^\s*\]\s*/, ''); // Remove any stray closing bracket at start

                            newContentNodes[0] = { ...firstTextNode, value: cleanedText };
                            return { ...newContent, content: newContentNodes };
                        }
                    }
                }
                return content;
            });

            // Apply base style and node-specific style
            // If nodeStyles.listItem contains a marker style, use it; otherwise, use default marker:text-green
            const hasCustomMarker = nodeStyles.listItem && nodeStyles.listItem.includes('marker:');
            const combinedStyle = `${hasCustomMarker ? '' : 'marker:text-green '}${nodeStyles.listItem || ''}`.trim();

            // If this has a cross marker, render with cross style
            if (hasCrossMarker) {
                return (
                    <li key={`list-item-${node.id}`} className="list-none pl-3 relative mb-2">
                        <div className="absolute left-[-25px] top-0 flex items-center">
                            <img src={CheckMarkRed} alt="Cross" className="w-5 h-5" />
                        </div>
                        {processedContent.map((content, index) => {
                            // For handling paragraph content with special rendering
                            if (content.nodeType === BLOCKS.PARAGRAPH) {
                                return (
                                    <React.Fragment key={`list-item-paragraph-${index}`}>
                                        {content.content?.map((contentNode, contentIndex) =>
                                            renderTextNode(contentNode, contentIndex, 'list-item', customMarks)
                                        )}
                                    </React.Fragment>
                                );
                            }

                            // For other content types, use the document renderer
                            return (
                                <React.Fragment key={`list-item-content-${index}`}>
                                    {documentToReactComponents(content, {
                                        renderNode: createRenderOptions(nodeStyles, referencesMap, customRenderers, customMarks).renderNode,
                                        renderMark: createRenderOptions(nodeStyles, referencesMap, customRenderers, customMarks).renderMark,
                                        renderText: createRenderOptions(nodeStyles, referencesMap, customRenderers, customMarks).renderText
                                    })}
                                </React.Fragment>
                            );
                        })}
                    </li>
                );
            }

            // If this has a checkmark marker, render with checkmark style
            if (hasCheckmarkMarker) {
                return (
                    <li key={`list-item-${node.id}`} className="list-none pl-3 relative mb-2">
                        <div className="absolute left-[-25px] top-0 flex items-center">
                            <img src={CheckMarkGreen} alt="Checkmark" className="w-[24px] h-6" />
                        </div>
                        {processedContent.map((content, index) => {
                            // For handling paragraph content with special rendering
                            if (content.nodeType === BLOCKS.PARAGRAPH) {
                                return (
                                    <React.Fragment key={`list-item-paragraph-${index}`}>
                                        {content.content?.map((contentNode, contentIndex) =>
                                            renderTextNode(contentNode, contentIndex, 'list-item', customMarks)
                                        )}
                                    </React.Fragment>
                                );
                            }

                            // For other content types, use the document renderer
                            return (
                                <React.Fragment key={`list-item-content-${index}`}>
                                    {documentToReactComponents(content, {
                                        renderNode: createRenderOptions(nodeStyles, referencesMap, customRenderers, customMarks).renderNode,
                                        renderMark: createRenderOptions(nodeStyles, referencesMap, customRenderers, customMarks).renderMark,
                                        renderText: createRenderOptions(nodeStyles, referencesMap, customRenderers, customMarks).renderText
                                    })}
                                </React.Fragment>
                            );
                        })}
                    </li>
                );
            }

            return (
                <li key={`list-item-${node.id}`} className={combinedStyle}>
                    {processedContent.map((content, index) => {
                        // For handling paragraph content with special rendering
                        if (content.nodeType === BLOCKS.PARAGRAPH) {
                            return (
                                <React.Fragment key={`list-item-paragraph-${index}`}>
                                    {content.content?.map((contentNode, contentIndex) =>
                                        renderTextNode(contentNode, contentIndex, 'list-item', customMarks)
                                    )}
                                </React.Fragment>
                            );
                        }

                        // For other content types, use the document renderer
                        return (
                            <React.Fragment key={`list-item-content-${index}`}>
                                {documentToReactComponents(content, {
                                    renderNode: createRenderOptions(nodeStyles, referencesMap, customRenderers, customMarks).renderNode,
                                    renderMark: createRenderOptions(nodeStyles, referencesMap, customRenderers, customMarks).renderMark,
                                    renderText: createRenderOptions(nodeStyles, referencesMap, customRenderers, customMarks).renderText
                                })}
                            </React.Fragment>
                        );
                    })}
                </li>
            );
        },
        // Handle embedded entries (references) - don't pass style parameter
        [BLOCKS.EMBEDDED_ENTRY]: (node) => {
            const targetId = node.data?.target?.sys?.id;
            if (!targetId || !referencesMap[targetId]) {
                return <div className="text-red_error text-sm">Missing embedded entry</div>;
            }

            return renderEmbeddedEntry(referencesMap[targetId], customRenderers);
        },
        // Handle embedded assets (images, videos, etc.) - use nodeStyles but not style
        [BLOCKS.EMBEDDED_ASSET]: (node) => {
            const targetId = node.data?.target?.sys?.id;
            if (!targetId || !referencesMap[targetId]) {
                return <div className="text-red_error text-sm">Missing embedded asset</div>;
            }

            const asset = referencesMap[targetId];

            // Handle different asset types
            if (asset.file?.contentType?.startsWith('image/')) {
                // Apply only node-specific style for images, not base style
                const imageStyle = `my-4 max-w-full ${nodeStyles.image || ''}`.trim();

                return (
                    <img
                        src={asset.file.url}
                        alt={asset.title || 'Embedded image'}
                        className={imageStyle}
                    />
                );
            }

            return null;
        },
        // Add horizontal rule renderer
        [BLOCKS.HR]: (node) => {
            // Check if there's a textColor in nodeStyles specifically for HR
            const hrColor = nodeStyles.hrColor || (textColor ? `border-${textColor?.toLowerCase()}` : 'text-black');
            const hrStyle = `hr-element border-t ${hrColor} ${nodeStyles.hr || ''}`.trim();
            if (textAlign?.toLowerCase() === 'center') {
                return <hr className={`${hrStyle} mx-auto w-[80px] border-2`.trim()} />;
            } else if (textAlign?.toLowerCase() === 'left') {
                return <hr className={`${hrStyle} mx-auto w-[80px] border-2`.trim()} />;
            } else {
                return <hr className={`${hrStyle}`.trim()} />;
            }
        },
        // Handle hyperlinks within paragraphs and other elements
        [INLINES.HYPERLINK]: (node, children) => {
            const { uri } = node.data;
            return (
                <Cta
                    displayType="Link"
                    href={uri}
                    label={children}
                    target={(typeof window === 'undefined' ? uri.startsWith('/') : uri.startsWith('/') || uri.includes(window.location.hostname)) ? "Same" : "New"}
                    className="text-primary hover:underline"
                />
            );
        },
    },
    renderMark: {
        // Add renderMark option to handle custom marks
        ...Object.entries(customMarks).reduce((acc, [markType, renderer]) => {
            if (typeof renderer === 'function') {
                acc[markType] = renderer;
            }
            return acc;
        }, {}),

        // Standard mark renderers
        [MARKS.BOLD]: text => <b>{text}</b>,
        [MARKS.ITALIC]: text => <i>{text}</i>,
        [MARKS.UNDERLINE]: text => <u>{text}</u>,
        [MARKS.CODE]: text => <code>{text}</code>,
        [MARKS.SUPERSCRIPT]: text => <sup>{text}</sup>,
        [MARKS.SUBSCRIPT]: text => <sub>{text}</sub>,
    },
});

// Add PropTypes definition at the end of the file
TextComponent.propTypes = {
    content: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    references: PropTypes.array,
    style: PropTypes.string,
    nodeStyles: PropTypes.object,
    customRenderers: PropTypes.object,
    customMarks: PropTypes.object,
    onError: PropTypes.func,
    marginTopDesktop: PropTypes.number,
    marginBottomDesktop: PropTypes.number,
    marginTopMobile: PropTypes.number,
    marginBottomMobile: PropTypes.number,
    paddingTopDesktop: PropTypes.number,
    paddingBottomDesktop: PropTypes.number,
    paddingTopMobile: PropTypes.number,
    paddingBottomMobile: PropTypes.number,
    paddingLeftDesktop: PropTypes.number,
    paddingRightDesktop: PropTypes.number,
    paddingLeftMobile: PropTypes.number,
    paddingRightMobile: PropTypes.number,
    maxWidthDesktop: PropTypes.number,
    maxWidthMobile: PropTypes.number,
    classOverlay: PropTypes.string,
    textAlign: PropTypes.string,
    textColor: PropTypes.string,
};

export default TextComponent;